import { useState, useRef, useEffect } from 'react'
import { useChat } from '../contexts/ChatContext'
import InfoModal from './InfoModal'

function ConversationItem({ conversation, isActive, onClick, onMenuClick }) {
  const [isHovered, setIsHovered] = useState(false)

  // Display conversation title
  const getDisplayTitle = () => {
    // If there's no title or it's empty, show default
    if (!conversation.title || conversation.title.trim() === '') {
      return 'محادثة جديدة'
    }

    // Otherwise show the actual title (trust the backend)
    return conversation.title
  }

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`
        group relative px-3 py-2.5 mb-1 rounded-lg cursor-pointer transition-all duration-150
        ${isActive
          ? 'bg-muted text-foreground'
          : 'hover:bg-muted/50 text-foreground'
        }
      `}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 text-sm truncate leading-relaxed">
          {getDisplayTitle()}
        </div>
        {(isHovered || isActive) && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onMenuClick(e, conversation.id)
            }}
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded hover:bg-background/50 transition-all duration-150 text-muted-foreground hover:text-foreground"
          >
            ⋮
          </button>
        )}
      </div>
    </div>
  )
}

function ContextMenu({ isOpen, position, onRename, onDelete, onClose }) {
  const menuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      ref={menuRef}
      style={{ top: position.y, left: position.x }}
      className="fixed z-50 bg-popover text-popover-foreground border border-border rounded-xl shadow-xl py-1 min-w-[150px]"
    >
      <button
        onClick={onRename}
        className="w-full px-4 py-2 text-right text-sm hover:bg-muted transition-all duration-150 flex items-center gap-2"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        <span>إعادة تسمية</span>
      </button>
      <button
        onClick={onDelete}
        className="w-full px-4 py-2 text-right text-sm hover:bg-destructive/10 text-destructive transition-all duration-150 flex items-center gap-2"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
        <span>حذف</span>
      </button>
    </div>
  )
}

export default function Sidebar() {
  const {
    conversations,
    loadingConversations,
    conversationId,
    sidebarCollapsed,
    setSidebarCollapsed,
    loadConversationById,
    loadConversations,
    renameConversation,
    deleteConversation,
    startNewConversation
  } = useChat()

  const [searchQuery, setSearchQuery] = useState('')
  const [contextMenu, setContextMenu] = useState({ isOpen: false, conversationId: null, position: { x: 0, y: 0 } })
  const searchTimeoutRef = useRef(null)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [showInfoModal, setShowInfoModal] = useState(false)

  const handleSearch = (query) => {
    setSearchQuery(query)

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadConversations(query)
    }, 300)
  }

  // Group conversations by date
  const groupConversationsByDate = (convs) => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const lastWeek = new Date(today)
    lastWeek.setDate(lastWeek.getDate() - 7)
    const lastMonth = new Date(today)
    lastMonth.setMonth(lastMonth.getMonth() - 1)

    const groups = {
      today: [],
      yesterday: [],
      lastWeek: [],
      lastMonth: [],
      older: []
    }

    convs.forEach(conv => {
      const date = new Date(conv.updated_at)
      if (date >= today) {
        groups.today.push(conv)
      } else if (date >= yesterday) {
        groups.yesterday.push(conv)
      } else if (date >= lastWeek) {
        groups.lastWeek.push(conv)
      } else if (date >= lastMonth) {
        groups.lastMonth.push(conv)
      } else {
        groups.older.push(conv)
      }
    })

    return groups
  }

  const groupedConversations = groupConversationsByDate(conversations)

  const handleMenuClick = (e, convId) => {
    setContextMenu({
      isOpen: true,
      conversationId: convId,
      position: { x: e.clientX, y: e.clientY }
    })
  }

  const handleRename = async () => {
    const conversation = conversations.find(c => c.id === contextMenu.conversationId)
    if (!conversation) return

    const newTitle = prompt('أدخل عنواناً جديداً:', conversation.title || '')
    if (newTitle && newTitle !== conversation.title) {
      try {
        await renameConversation(contextMenu.conversationId, newTitle)
      } catch (error) {
        alert('فشل إعادة التسمية')
      }
    }
    setContextMenu({ ...contextMenu, isOpen: false })
  }

  const handleDelete = async () => {
    if (confirm('هل أنت متأكد من حذف هذه المحادثة؟ لا يمكن التراجع عن هذا الإجراء.')) {
      try {
        await deleteConversation(contextMenu.conversationId)
      } catch (error) {
        alert('فشل الحذف')
      }
    }
    setContextMenu({ ...contextMenu, isOpen: false })
  }

  return (
    <>
      <aside
        className={`
          ${sidebarCollapsed ? 'w-[70px]' : 'w-[280px]'}
          bg-background border-r border-border/50 flex flex-col transition-all duration-300 ease-out flex-shrink-0
          max-lg:fixed max-lg:top-0 max-lg:bottom-0 max-lg:z-50 max-lg:w-[280px] max-lg:shadow-2xl
          ${sidebarCollapsed ? 'max-lg:-left-[280px]' : 'max-lg:left-0'}
        `}
      >
        {/* Logo and Toggle */}
        <div className={`flex items-center ${sidebarCollapsed ? 'justify-center max-lg:justify-between' : 'justify-between'} px-4 py-4 border-b border-border/50`}>
          <div className={`flex items-center gap-3 ${sidebarCollapsed ? 'hidden max-lg:flex' : ''}`}>
            <div className="flex-shrink-0">
              <img
                src="/img/logo_light.svg"
                alt="AIlex"
                className="h-8 w-auto block dark:hidden"
              />
              <img
                src="/img/logo_dark.svg"
                alt="AIlex"
                className="h-8 w-auto hidden dark:block"
              />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-xs text-muted-foreground">Beta | تجريبية</span>
            </div>
          </div>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-all duration-150"
            title={sidebarCollapsed ? "فتح القائمة" : "إغلاق القائمة"}
          >
            {sidebarCollapsed ? '▶' : '◀'}
          </button>
        </div>

        {/* New Conversation Button */}
        <div className="px-3 py-3 border-b border-border/50">
          <button
            onClick={() => {
              startNewConversation()
              // Close sidebar on mobile after clicking
              if (window.innerWidth <= 1024) {
                setSidebarCollapsed(true)
              }
            }}
            className={`
              w-full px-4 py-2.5 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 active:scale-95 transition-all duration-150 font-semibold shadow-md flex items-center justify-center gap-2
              ${sidebarCollapsed ? 'px-2 max-lg:px-4' : ''}
            `}
            title="محادثة جديدة"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className={sidebarCollapsed ? 'hidden max-lg:inline' : ''}>محادثة جديدة</span>
          </button>
        </div>

        {/* Search */}
        <div className={`px-3 py-3 border-b border-border/50 ${sidebarCollapsed ? 'hidden max-lg:block' : ''}`}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="ابحث في المحادثات..."
            className="w-full px-3 py-2 text-sm border border-input bg-card rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-150"
            style={{ direction: 'rtl' }}
          />
        </div>

        {/* Conversation List */}
        <div className={`flex-1 overflow-y-auto px-2 py-2 ${sidebarCollapsed ? 'hidden max-lg:block' : ''}`}>
            {loadingConversations ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                جاري التحميل...
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                لا توجد محادثات سابقة
              </div>
            ) : (
            <>
              {groupedConversations.today.length > 0 && (
                <div className="mb-4">
                  <h4 className="px-3 py-1 text-xs font-semibold text-muted-foreground">اليوم</h4>
                  {groupedConversations.today.map((conv) => (
                    <ConversationItem
                      key={conv.id}
                      conversation={conv}
                      isActive={conv.id === conversationId}
                      onClick={() => loadConversationById(conv.id)}
                      onMenuClick={handleMenuClick}
                    />
                  ))}
                </div>
              )}
              {groupedConversations.yesterday.length > 0 && (
                <div className="mb-4">
                  <h4 className="px-3 py-1 text-xs font-semibold text-muted-foreground">أمس</h4>
                  {groupedConversations.yesterday.map((conv) => (
                    <ConversationItem
                      key={conv.id}
                      conversation={conv}
                      isActive={conv.id === conversationId}
                      onClick={() => loadConversationById(conv.id)}
                      onMenuClick={handleMenuClick}
                    />
                  ))}
                </div>
              )}
              {groupedConversations.lastWeek.length > 0 && (
                <div className="mb-4">
                  <h4 className="px-3 py-1 text-xs font-semibold text-muted-foreground">آخر 7 أيام</h4>
                  {groupedConversations.lastWeek.map((conv) => (
                    <ConversationItem
                      key={conv.id}
                      conversation={conv}
                      isActive={conv.id === conversationId}
                      onClick={() => loadConversationById(conv.id)}
                      onMenuClick={handleMenuClick}
                    />
                  ))}
                </div>
              )}
              {groupedConversations.lastMonth.length > 0 && (
                <div className="mb-4">
                  <h4 className="px-3 py-1 text-xs font-semibold text-muted-foreground">آخر 30 يوم</h4>
                  {groupedConversations.lastMonth.map((conv) => (
                    <ConversationItem
                      key={conv.id}
                      conversation={conv}
                      isActive={conv.id === conversationId}
                      onClick={() => loadConversationById(conv.id)}
                      onMenuClick={handleMenuClick}
                    />
                  ))}
                </div>
              )}
              {groupedConversations.older.length > 0 && (
                <div className="mb-4">
                  <h4 className="px-3 py-1 text-xs font-semibold text-muted-foreground">قديم</h4>
                  {groupedConversations.older.map((conv) => (
                    <ConversationItem
                      key={conv.id}
                      conversation={conv}
                      isActive={conv.id === conversationId}
                      onClick={() => loadConversationById(conv.id)}
                      onMenuClick={handleMenuClick}
                    />
                  ))}
                </div>
              )}
              </>
            )}
        </div>

        {/* Footer - Info and Settings Buttons */}
        <div className={`mt-auto px-3 py-3 border-t border-border/50 ${sidebarCollapsed ? 'space-y-2' : 'space-y-2'}`}>
          {/* Info Button */}
          <button
            onClick={() => setShowInfoModal(true)}
            className={`
              w-full flex items-center ${sidebarCollapsed ? 'justify-center max-lg:justify-center' : 'justify-center'} gap-2 px-4 py-2.5 border border-border rounded-xl hover:bg-muted active:scale-95 transition-all duration-150
              ${sidebarCollapsed ? 'px-2 max-lg:px-4' : ''}
            `}
            title="معلومات"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className={`font-medium ${sidebarCollapsed ? 'hidden max-lg:inline' : ''}`}>معلومات</span>
          </button>

          {/* Settings Button */}
          <button
            onClick={() => setShowSettingsModal(true)}
            className={`
              w-full flex items-center ${sidebarCollapsed ? 'justify-center max-lg:justify-center' : 'justify-center'} gap-2 px-4 py-2.5 border border-border rounded-xl hover:bg-muted active:scale-95 transition-all duration-150
              ${sidebarCollapsed ? 'px-2 max-lg:px-4' : ''}
            `}
            title="الإعدادات"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className={`font-medium ${sidebarCollapsed ? 'hidden max-lg:inline' : ''}`}>الإعدادات</span>
          </button>
        </div>
      </aside>

      {/* Context Menu */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        onRename={handleRename}
        onDelete={handleDelete}
        onClose={() => setContextMenu({ ...contextMenu, isOpen: false })}
      />

      {/* Info Modal */}
      <InfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
      />

      {/* Settings Modal */}
      {showSettingsModal && (
        <SettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
        />
      )}
    </>
  )
}

// Import SettingsModal at the top of the file
import SettingsModal from './SettingsModal'
